import { useEffect } from "react";

import {
  activePutLeaveMessage,
  isInternalNavigationAnchor,
} from "./activeByteTransferGuard";

/**
 * Protects the unsafe window while Song Uploader is mid-PUT (phase "uploading").
 *
 * - beforeunload: refresh / tab close / hard leave (browser may hide custom text)
 * - capture-phase click on internal <a>: SPA Links do not fire beforeunload under
 *   BrowserRouter (no data-router useBlocker available without App migration)
 *
 * Attach only while `active` is true; cleanup removes both listeners immediately
 * when the last PUT completes (do not extend for visual SAFE hold).
 * activePutCount drives singular/plural leave confirmation only.
 */
export function useActiveByteTransferGuard(
  active: boolean,
  activePutCount: number = 1,
): void {
  useEffect(() => {
    if (!active) return undefined;

    const leaveMessage = activePutLeaveMessage(Math.max(1, activePutCount));

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = leaveMessage;
      return leaveMessage;
    };

    const onDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (!isInternalNavigationAnchor(anchor)) return;
      const ok = window.confirm(leaveMessage);
      if (!ok) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onDocumentClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [active, activePutCount]);
}
